import Vue from 'vue';
import { axios, blueApi } from '@/axios.config';
import { mockAdapter } from '@/mocks/axiosMockAdapter.config';
import moment from 'moment';

export const state = {
  accounts: {
    data: {},
    request: {},
  },

  mappedAccountsData: [],

  // Azure account map has non-usage type for showing Adjusting Entries.
  nonusageTypeAccountMap: {},

  originalAzureFees: {},
  fees: {},
  feesRequests: {},

  bulkCsv: '',
  selectedId: null,
  details: {},
  requests: {},
};

export const actions = {
  /**
   * Fetch account list
   * force = false:Will request if No accounts.
   * @param state
   * @param commit
   * @param rootState
   * @param rootGetters
   * @param force {boolean} - default = false
   * @return {Promise}
   */
  fetchAccounts({ commit, getters, rootState }, { force = false }) {
    if (!force) {
      if (getters.getAccounts.length) return Promise.resolve();
    }

    const vendor = rootState.vendor;
    if (vendor === 'azure') {
      const url = `cost/v1/azure/accounts`;
      const cancelToken = new blueApi.CancelToken(() => {});

      commit('fetchAccountsStart', cancelToken);
      return blueApi
        .get(url)
        .then(({ data }) => {
          // Check and convert response data:
          // In case data only has 1 row, it will be converted to object type automatically,
          // so need to check data type before adding it to record array.
          let records = [];
          if (typeof data === 'string' || data instanceof String) {
            records.push(...data.split('\n'));
          } else {
            records.push(JSON.stringify(data));
          }
          const { usageTypeAccounts, nonusageTypeAccountMap } = convertAzureAccountData(records);
          commit('updateAccounts', { data: usageTypeAccounts, vendor });
          commit('updateNonusageTypeAccountMap', nonusageTypeAccountMap);

          commit('fetchAccountsSucceed');

          if (records.length === 0) return Promise.reject('No accounts');

          return Promise.resolve();
        })
        .catch(err => {
          if (blueApi.isCancel(err)) {
            commit('fetchAccountsCanceled');
            return;
          }

          commit('fetchAccountsFailed', err);
          throw err;
        });
    } else {
      const url = `/account`;
      const cancelToken = new axios.CancelToken(() => {});

      mockAdapter(axios, 'get', url, 'getAccounts', 200);

      commit('fetchAccountsStart', cancelToken);
      return axios
        .get(url, { params: { vendor: vendor } })
        .then(({ data }) => {
          commit('updateAccounts', { data, vendor });

          // FIXME: should check to handle this one
          // causing very long loading time if account have many data
          // data.forEach(account => {
          //   commit('updateSelectedId', account.account_id);
          //   // dispatch('fetchDetail', { force: true }); // FIXME: causing very long loading time
          //   commit('updateSelectedId', null);
          // });
          commit('fetchAccountsSucceed');

          if (data.length === 0) return Promise.reject('No accounts');

          return Promise.resolve();
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            commit('fetchAccountsCanceled');
            return;
          }

          commit('fetchAccountsFailed', err);
          throw err;
        });
    }
  },

  fetchAzureSubscriptions({ commit, getters }, { force = false }) {
    if (!force) {
      if (getters.getAccounts.length) return Promise.resolve();
    }

    const url = `cost/v1/azure/accounts`;
    const cancelToken = new blueApi.CancelToken(() => {});

    commit('fetchAccountsStart', cancelToken);
    return blueApi
      .get(url)
      .then(({ data }) => {
        // Check and convert response data:
        // In case data only has 1 row, it will be converted to object type automatically,
        // so need to check data type before adding it to record array.
        let records = [];
        if (typeof data === 'string' || data instanceof String) {
          records.push(...data.split('\n'));
        } else {
          records.push(JSON.stringify(data));
        }
        const { usageTypeAccounts, nonusageTypeAccountMap } = convertAzureSubscriptionData(records);
        commit('updateAccounts', { data: usageTypeAccounts, vendor: 'azure' });
        commit('updateNonusageTypeAccountMap', nonusageTypeAccountMap);
        commit('fetchAccountsSucceed');

        if (records.length === 0) return Promise.reject('No accounts');
        return Promise.resolve();
      })
      .catch(err => {
        if (blueApi.isCancel(err)) {
          commit('fetchAccountsCanceled');
          return;
        }

        commit('fetchAccountsFailed', err);
        throw err;
      });
  },

  fetchDetail({ state, commit, rootState }, { force = false }) {
    const id = state.selectedId;
    if (!id) return false;

    if (!force) {
      if (state.details[id]) return false;
    }

    const url = `/account/describe/${id}`;
    const reqHash = state.selectedId;

    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    const vendor = rootState.vendor;
    if (vendor === 'azure') {
      let accounts = state.accounts.data[vendor] ? state.accounts.data[vendor] : [];
      for (const acct of accounts) {
        if (acct.account_id === id) {
          const data = acct;
          commit('fetchSucceed', { reqHash });
          commit('updateAccountDetail', { data, reqHash });
          commit('usageReports/updateDetail', { data, reqHash }, { root: true });
          break;
        }
      }
      return Promise.resolve();
    }

    const cancelToken = new axios.CancelToken(() => {});

    mockAdapter(axios, 'get', url, 'getAccountDetail', 200);

    commit('fetchStart', { id, reqHash, cancelToken });

    return axios
      .get(url, { params: { vendor: rootState.vendor } })
      .then(({ data }) => {
        commit('fetchSucceed', { reqHash });
        commit('updateAccountDetail', { data, reqHash });
        commit('usageReports/updateDetail', { data, reqHash }, { root: true });

        return Promise.resolve();
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          commit('fetchCanceled', { reqHash });
          return;
        }

        commit('fetchFailed', { reqHash, err });
        throw err;
      });
  },

  /**
   * GET /REPORT/FEES
   */
  fetchFees({ state, commit, rootState, rootGetters }, month) {
    const reqHash = `${state.selectedId}-${month}`;

    if (state.fees[reqHash] && !state.fees[reqHash].inProgress) {
      Vue.delete(state.fees, reqHash);
    }

    if (state.feesRequests[reqHash] && state.feesRequests[reqHash].inProgress) {
      return;
    }

    const cancelToken = new axios.CancelToken(() => {});
    commit('fetchFeesStart', { reqHash, cancelToken });

    if (rootState.vendor === 'azure') {
      const reqBody = {
        groupId: rootState.userState.companyId,
        startTime: moment(month)
          .startOf('month')
          .format('YYYYMMDD'),
        endTime: moment(month)
          .endOf('month')
          .format('YYYYMMDD'),
      };
      const selectedAccount = rootGetters['usageReports/getSelectedAccount'];
      const azureCustomerId = selectedAccount.azure_customer_id;
      const nonusageTypeAccount = state.nonusageTypeAccountMap[azureCustomerId];
      if (nonusageTypeAccount) {
        reqBody.accountId = nonusageTypeAccount.account_ids;
      }
      if (reqBody.accountId) {
        reqBody.accountId += ',' + selectedAccount.account_ids + ',' + selectedAccount.account_id;
      } else {
        reqBody.accountId = selectedAccount.account_ids + ',' + selectedAccount.account_id;
      }
      return blueApi
        .post(`cost/v1/azure/adjustments:read`, reqBody)
        .then(({ data }) => {
          // Check and convert response data:
          // In case data only has 1 row, it will be converted to object type automatically,
          // so need to check data type before adding it to record array.
          let records = [];
          if (typeof data === 'string' || data instanceof String) {
            records.push(...data.split('\n'));
          } else {
            records.push(JSON.stringify(data));
          }
          const { originalItems, generatedItems } = generateAzureFees(records);
          commit('fetchFeesSucceed', { reqHash });
          commit('updateOriginalAzureFees', { feeItems: originalItems, reqHash });
          commit('updateFees', {
            vendor: 'azure',
            data: [{ month: month, account: generatedItems }],
            reqHash,
          });
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            commit('fetchFeesCanceled', { reqHash });
            return;
          }

          commit('fetchFeesFailed', { reqHash, err });
          throw err;
        });
    } else {
      const reqBody = {
        accountId: state.selectedId,
        month: month.replace('-', ''),
        feeType: '',
      };
      return blueApi
        .post('/billing/v1/aws/invoiceadjustments:read', reqBody)
        .then(res => {
          const records = [];
          if (typeof res.data === 'string' || res.data instanceof String) {
            records.push(...res.data.split('\n'));
          } else {
            records.push(JSON.stringify(res.data));
          }
          const data = generateAWSFees(records, month, state.selectedId);

          commit('fetchFeesSucceed', { reqHash });
          commit('updateFees', { vendor: rootState.vendor, data, reqHash });
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            commit('fetchFeesCanceled', { reqHash });
            return;
          }

          commit('fetchFeesFailed', { reqHash, err });
          throw err;
        });
    }
  },

  fetchReportBulkCsv({ commit, rootState }, month) {
    return axios
      .get(`/report/bulk_csv`, {
        params: {
          month: month,
          vendor: rootState.vendor,
          type: rootState.userState.dataStyle,
        },
      })
      .then(({ data }) => {
        commit('updateBulkCsv', data);

        if (!data) {
          return Promise.reject('No data');
        }
      })
      .catch(() => {
        return Promise.reject('No data');
      });
  },

  /**
   * PUT /ACCOUNT/{ACCOUNT_ID}
   */
  putAccountName({ state, rootState }, accountName) {
    let params = {
      account_name: accountName,
      vendor: rootState.vendor,
    };

    return axios.put(`/account/${state.selectedId}`, params);
  },

  /**
   * PUT /ACCOUNT/{ACCOUNT_ID}/BUDGET
   */
  putBudget({ state, rootState }, arg) {
    const budget = arg.budget;
    const notification = arg.notification;

    let params = {
      budget: {
        daily: budget.daily ? Number(budget.daily) : 0,
        previous_day: budget.previous_day ? Number(budget.previous_day) : 0,
        monthly: budget.monthly ? Number(budget.monthly) : 0,
      },
      notification: notification,
      type: arg.type,
      vendor: rootState.vendor,
    };

    return axios.put(`/account/${state.selectedId}/budget`, params);
  },

  removeFeeRequest({ commit }, reqHash) {
    commit('updateFeesRequest', { reqHash });
  },
};

export const mutations = {
  updateFeesRequest(state, { reqHash }) {
    Vue.delete(state.feesRequests, reqHash);
  },
  updateMappedAccountsData(state, data) {
    Vue.set(state.mappedAccountsData, data);
  },

  updateAccounts(state, { data, vendor }) {
    Vue.set(state.accounts.data, vendor, data);
  },

  updateSelectedId(state, id) {
    state.selectedId = id;
  },

  updateAccountDetail(state, { data, reqHash }) {
    Vue.set(state.details, reqHash, data);
  },

  updateFees(state, { data, reqHash, vendor }) {
    const accounts = data[0]['account'];
    if (accounts) {
      if (vendor === 'azure') {
        Vue.set(state.fees, reqHash, accounts);
      } else {
        const fees = accounts[0];
        Vue.set(state.fees, reqHash, fees);
      }
    } else {
      Vue.set(state.fees, reqHash, []);
    }
  },

  updateOriginalAzureFees(state, { feeItems, reqHash }) {
    Vue.set(state.originalAzureFees, reqHash, feeItems);
  },

  updateBulkCsv(state, data) {
    state.bulkCsv = data;
  },

  updateNonusageTypeAccountMap(state, data) {
    state.nonusageTypeAccountMap = data;
  },

  reset(state) {
    state.selectedId = '';
    state.accounts.data = {};
  },

  // State controls(Account Detail)
  fetchStart(state, { reqHash, cancelToken }) {
    Vue.set(state.requests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  fetchSucceed(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  fetchCanceled(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  fetchFailed(state, { reqHash, err }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },

  // State controls(Account Detail)
  fetchFeesStart(state, { reqHash, cancelToken }) {
    Vue.set(state.feesRequests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  fetchFeesSucceed(state, { reqHash }) {
    Vue.set(state.feesRequests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  fetchFeesCanceled(state, { reqHash }) {
    Vue.set(state.feesRequests, reqHash, {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  fetchFeesFailed(state, { reqHash, err }) {
    Vue.set(state.feesRequests, reqHash, {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },

  // State controls(Accounts)
  fetchAccountsStart(state, cancelToken) {
    Vue.set(state.accounts, 'request', {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  fetchAccountsSucceed(state) {
    Vue.set(state.accounts, 'request', {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  fetchAccountsCanceled(state) {
    Vue.set(state.accounts, 'request', {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  fetchAccountsFailed(state, err) {
    Vue.set(state.accounts, 'request', {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },
};

export const getters = {
  getAccounts(state, getters, rootState) {
    const vendor = rootState.vendor;
    let accounts = state.accounts.data[vendor] ? state.accounts.data[vendor] : [];

    // Grouping by payer
    accounts = accounts.sort(function(a, b) {
      if (a.payer_id < b.payer_id) return -1;
      if (a.payer_id > b.payer_id) return 1;
      return b.payer - a.payer;
    });

    return accounts;
  },

  getCurrentAccountId(state) {
    return state.selectedId ? state.selectedId : null;
  },

  getDetail(state) {
    const id = state.selectedId;
    return state.details[id] ? state.details[id] : null;
  },

  getDetailAccountName(state, getters) {
    const detail = getters.getDetail;
    return detail ? detail.account_name : '-';
  },

  getDetailAccountId(state, getters) {
    const detail = getters.getDetail;
    return detail ? detail.account_id : '-';
  },

  getNonusageTypeAccountMap(state) {
    return state.nonusageTypeAccountMap;
  },

  getAccountDetails(state) {
    return state.details;
  },

  mappingAccountData(state, getters, rootState, rootGetters) {
    return getters.getAccounts.map(val => {
      const badges = [];
      if (val.payer) badges.push({ type: 'dark', value: 'Payer', message: 'Payer account' });

      const costType = rootGetters['user/getCostType'];

      const notification = state.details[val.account_id]
        ? state.details[val.account_id][`${costType.replace('cost', 'notification')}`]
        : null;

      const willNotify = notification
        ? notification['daily'] || notification['monthly'] || notification['previous_day']
        : false;

      if (willNotify)
        badges.push({
          type: 'warning',
          icon: 'notifications_active',
          message: 'Notification enabled',
        });

      return {
        account_id: val.account_id,
        account_name: val.account_name,
        azure_customer_id: val.azure_customer_id,
        badges: badges,
        account_ids: val.account_ids,
        is_subscription: val.is_subscription,
      };
    });
  },
};

export const modules = {
  azure: {
    namespaced: true,
    actions: {
      fetchReportBulkCsv({ commit }, month) {
        return axios
          .get(`/azure/v3/report/period/${month}`)
          .then(({ data }) => {
            if (data.payload.link.file_found) {
              commit('accounts/updateBulkCsv', data.payload.link.link, { root: true });
            } else {
              throw new Error('unavailable');
            }
          })
          .catch(err => {
            throw err;
          });
      },
    },
  },
};

function generateAzureFees(records) {
  const originalItems = [];
  for (const record of records) {
    if (!record) {
      continue;
    }
    const jsonObj = JSON.parse(record);
    if (!jsonObj) {
      continue;
    }
    if (!jsonObj.result) {
      continue;
    }
    if (jsonObj.result.azure) {
      originalItems.push(jsonObj.result.azure);
    }
  }

  const accountFeeMap = {};
  for (const feeItem of originalItems) {
    const items = accountFeeMap[feeItem.account];
    if (items) {
      items.push(feeItem);
      accountFeeMap[feeItem.account] = items;
    } else {
      accountFeeMap[feeItem.account] = [feeItem];
    }
  }

  const marketplace = [];
  const softwareLicense = [];
  const ri = [];
  const others = [];
  for (const accountId in accountFeeMap) {
    const feeItems = accountFeeMap[accountId];
    for (const feeItem of feeItems) {
      const convertedItem = {
        id: accountId,
        name: accountId,
        'lineItem/UsageAccountId': feeItem.account,
        'lineItem/CurrencyCode': feeItem.targetCurrency,
        'lineItem/LineItemDescription': feeItem.description,
        'lineItem/ProductCode': feeItem.serviceName,
        'lineItem/UnblendedCost': feeItem.targetCost,
        'lineItem/UsageStartDate': feeItem.date,
        'product/ProductName': feeItem.productName,
        'identity/TimeInterval': feeItem.timeInterval,
      };
      const feeType = detectAzureFeeItemType(feeItem);
      switch (feeType) {
        case 'marketplace':
          marketplace.push(convertedItem);
          break;
        case 'software_license':
          softwareLicense.push(convertedItem);
          break;
        case 'ri':
          ri.push(convertedItem);
          break;
        default:
          others.push(convertedItem);
          break;
      }
    }
  }
  const generatedItems = {
    marketplace: marketplace,
    software_license: softwareLicense,
    ri: ri,
    others: others,
  };
  return { originalItems, generatedItems };
}

function generateAWSFees(records, month, selectedId) {
  let parsedRecords = [];
  if (records.length === 1) {
    try {
      for (let i = 0; i < records.length; i++) {
        parsedRecords[i] = JSON.parse(records[i]);
      }
    } catch (e) {
      parsedRecords = [];
    }
  } else {
    for (let i = 0; i < records.length - 1; i++) {
      parsedRecords[i] = JSON.parse(records[i]);
    }
  }
  const data = [
    {
      month: month,
      account: [
        {
          id: selectedId,
          Credit: [],
          Fee: [],
          Refund: [],
        },
      ],
    },
  ];
  if (parsedRecords.length) {
    parsedRecords.forEach(({ result }) => {
      if (!data[0].account[0][result.feeType]) {
        data[0].account[0][result.feeType] = [
          {
            'lineItem/LineItemDescription': result.description,
            'lineItem/ProductCode': result.productCode,
            'product/ProductName': result.productName,
            'lineItem/UsageAccountId': result.usageAccountId,
            'lineItem/CurrencyCode': result.currencyCode,
            'bill/PayerAccountId': result.payerAccountId,
            'lineItem/UnblendedCost': result.cost,
            'identity/TimeInterval': result.timeInterval,
            'lineItem/UsageStartDate': result.usageStartDate,
          },
        ];
      } else {
        data[0].account[0][result.feeType].push({
          'lineItem/LineItemDescription': result.description,
          'lineItem/ProductCode': result.productCode,
          'product/ProductName': result.productName,
          'lineItem/UsageAccountId': result.usageAccountId,
          'lineItem/CurrencyCode': result.currencyCode,
          'bill/PayerAccountId': result.payerAccountId,
          'lineItem/UnblendedCost': result.cost,
          'identity/TimeInterval': result.timeInterval,
          'lineItem/UsageStartDate': result.usageStartDate,
        });
      }
    });
  }

  return data;
}

export function detectAzureFeeItemType(feeItem) {
  if (feeItem.billingType === 'MARKETPLACE') {
    return 'marketplace';
  }

  if (
    feeItem.serviceName === 'Software License' ||
    feeItem.serviceName === 'New-Commerce ERP Software License' ||
    feeItem.serviceName === 'MSRP Software License'
  ) {
    return 'software_license';
  }

  if (feeItem.serviceName.includes('Virtual Machines')) {
    return 'ri';
  }

  return 'other';
}

/*
Converts Azure account data, groups account ids.

Sample account data:
{
    result": {
        "vendor": "azure",
        "id": "16FE1838-26FA-4D8A-819D-A8972C728908",
        "name": "pcz5020DVYKHM",
        "orgId": "MSP-5b6a504c2abf5",
        "billingInternalId": "RSXNQBYj",
        "metadata": [
            {
                "key": "accountId",
                "value": "1069565"
            },
            {
                "key": "azureCustomerId",
                "value": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
            },
            {
                "key": "azurePlanId",
                "value": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
            }
        ]
    }
}{
    "result": {
        "vendor": "azure",
        "id": "2A4F71BB-FB97-40D0-9D3C-71BAE299A427",
        "name": "pcz5020DVYKHM",
        "orgId": "MSP-5b6a504c2abf5",
        "billingInternalId": "RSXNQBYj",
        "metadata": [
            {
                "key": "accountId",
                "value": "1069565"
            },
            {
                "key": "azureCustomerId",
                "value": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
            },
            {
                "key": "azurePlanId",
                "value": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
            }
        ]
    }
}
Convert to:
  [
    {
      "payer_id": "1069565",
      "account_id": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE", // Azure customer id
      "account_name": "pcz5020DVYKHM",
      "account_ids": "16FE1838-26FA-4D8A-819D-A8972C728908,2A4F71BB-FB97-40D0-9D3C-71BAE299A427", // Ripple ids
      "16FE1838-26FA-4D8A-819D-A8972C728908": {
        "azure_plan_id": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
      },
      "2A4F71BB-FB97-40D0-9D3C-71BAE299A427": {
        "azure_plan_id": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
      }
    }
  ]
*/
export function convertAzureAccountData(records) {
  const originalItems = [];
  for (const record of records) {
    if (!record) {
      continue;
    }
    const jsonObj = JSON.parse(record);
    if (!jsonObj) {
      continue;
    }
    if (jsonObj.result) {
      const item = jsonObj.result;
      // Flatten metadata
      for (const metadata of item.metadata) {
        item[metadata.key] = metadata.value;
      }
      originalItems.push(item);
    }
  }

  const usageTypeAccounts = [];
  const usageTypeAccountMap = {};
  const nonusageTypeAccountMap = {};
  for (const acctItem of originalItems) {
    const azureCustomerId = acctItem.azureCustomerId;
    const azurePlanId = acctItem.azurePlanId;
    const payerId = acctItem.accountId;
    const rippleCustomerId = acctItem.id;
    let acctName = acctItem.name;
    if (!acctName) {
      acctName = azureCustomerId;
    }
    const key = azureCustomerId;
    if (azurePlanId) {
      const account = usageTypeAccountMap[key] || {
        account_name: acctName,
        payer_id: payerId,
      };
      account.account_id = azureCustomerId;
      account.azure_customer_id = azureCustomerId;
      account[rippleCustomerId] = {
        azure_plan_id: azurePlanId,
      };
      account.account_ids = account.account_ids
        ? account.account_ids + ',' + rippleCustomerId
        : rippleCustomerId;
      usageTypeAccountMap[key] = account;
    } else {
      const account = nonusageTypeAccountMap[key] || {};
      account.account_id = azureCustomerId;
      account.account_ids = account.account_ids
        ? account.account_ids + ',' + rippleCustomerId
        : rippleCustomerId;
      nonusageTypeAccountMap[key] = account;
    }
  }
  for (const key in usageTypeAccountMap) {
    usageTypeAccounts.push(usageTypeAccountMap[key]);
  }
  return { usageTypeAccounts, nonusageTypeAccountMap };
}

/*
Converts Azure subscription data, groups subscription ids.

Sample account data:
{
    result": {
        "vendor": "azure",
        "id": "16FE1838-26FA-4D8A-819D-A8972C728908",
        "name": "pcz5020DVYKHM",
        "orgId": "MSP-5b6a504c2abf5",
        "billingInternalId": "RSXNQBYj",
        "metadata": [
            {
                "key": "accountId",
                "value": "1069565"
            },
            {
                "key": "azureCustomerId",
                "value": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
            },
            {
                "key": "azurePlanId",
                "value": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
            }
        ]
    }
}{
    "result": {
        "vendor": "azure",
        "id": "2A4F71BB-FB97-40D0-9D3C-71BAE299A427",
        "name": "pcz5020DVYKHM",
        "orgId": "MSP-5b6a504c2abf5",
        "billingInternalId": "RSXNQBYj",
        "metadata": [
            {
                "key": "accountId",
                "value": "1069565"
            },
            {
                "key": "azureCustomerId",
                "value": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
            },
            {
                "key": "azurePlanId",
                "value": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7"
            }
        ]
    }
}
Convert to:
  [
    {
      "payer_id": "1069565",
      "account_id": "7688DE47-F2C8-206B-35A9-C6F5ADDD7EB7", // Azure plan id
      "account_name": "pcz5020DVYKHM",
      "account_ids": "16FE1838-26FA-4D8A-819D-A8972C728908,2A4F71BB-FB97-40D0-9D3C-71BAE299A427", // Ripple ids
      "16FE1838-26FA-4D8A-819D-A8972C728908": {
        "azure_customer_id": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
      }
      "2A4F71BB-FB97-40D0-9D3C-71BAE299A427": {
        "azure_customer_id": "70B99C9E-3EF3-4F5F-90E9-C082975B6EAE"
      }
    }
  ]
*/
export function convertAzureSubscriptionData(records) {
  const originalItems = [];
  for (const record of records) {
    if (!record) {
      continue;
    }
    const jsonObj = JSON.parse(record);
    if (!jsonObj) {
      continue;
    }
    if (jsonObj.result) {
      const item = jsonObj.result;
      // Flatten metadata
      for (const metadata of item.metadata) {
        item[metadata.key] = metadata.value;
      }
      originalItems.push(item);
    }
  }

  const usageTypeAccounts = [];
  const usageTypeAccountMap = {};
  const nonusageTypeAccountMap = {};
  for (const acctItem of originalItems) {
    const azureCustomerId = acctItem.azureCustomerId;
    const azurePlanId = acctItem.azurePlanId;
    const payerId = acctItem.accountId;
    const rippleCustomerId = acctItem.id;
    let acctName = acctItem.name;
    if (!acctName) {
      acctName = azureCustomerId;
    }
    const key = azureCustomerId + azurePlanId;
    if (azurePlanId) {
      const account = usageTypeAccountMap[key] || {
        account_name: acctName,
        payer_id: payerId,
      };
      account.account_id = azurePlanId;
      account.azure_customer_id = azureCustomerId;
      account.is_subscription = true;
      account[rippleCustomerId] = {
        azure_customer_id: azureCustomerId,
      };
      account.account_ids = account.account_ids
        ? account.account_ids + ',' + rippleCustomerId
        : rippleCustomerId;
      usageTypeAccountMap[key] = account;
    } else {
      const account = nonusageTypeAccountMap[key] || {};
      account.account_id = azureCustomerId;
      account.account_ids = account.account_ids
        ? account.account_ids + ',' + rippleCustomerId
        : rippleCustomerId;
      nonusageTypeAccountMap[key] = account;
    }
  }
  for (const key in usageTypeAccountMap) {
    usageTypeAccounts.push(usageTypeAccountMap[key]);
  }
  return { usageTypeAccounts, nonusageTypeAccountMap };
}
